import { useState, useEffect } from 'react';
import { Building, Check, X, Clock, Users, MapPin, Wifi, Car, Coffee, Wind, Dumbbell, Heart, Briefcase, Users as UsersIcon, Trees, Loader } from 'lucide-react';
import { useTranslation } from '../../../i18n/i18n';

const HotelView = ({ product, lang }) => {
    const { t } = useTranslation(lang);

    const API_BASE_URL = process.env.REACT_APP_API_BASE_URL;

    const getFieldValue = (fieldArray, fallback = '') => {
        if (!fieldArray || fieldArray.length === 0) return fallback;
        const value = fieldArray[0][lang] || fieldArray[0].en || fieldArray[0].fr || fieldArray[0].es;
        return value || fallback;
    };

    const formatTime = (timeString) => {
        if (!timeString) return t('notSpecified');
        return timeString.split(':').slice(0, 2).join(':');
    };

    const getServiceLabel = (serviceType, serviceId, price = 0) => {
        const serviceMaps = {
            welcome_procedure: {
                1: { name: t('physicalCheckin') },
                2: { name: t('virtualCheckin') }
            },
            internet_service: {
                1: { name: t('wifiFree') },
                2: { name: t('wifiPaid') }
            },
            parking_service: {
                1: { name: t('parkingFree') },
                2: { name: t('parkingPaid') }
            },
            swimming_pool_service: {
                1: { name: t('outdoorPool') },
                2: { name: t('indoorPool') }
            }
        };

        const service = serviceMaps[serviceType]?.[serviceId];
        if (!service) return `${serviceType} ${serviceId}`;

        let label = service.name;
        if (price > 0) {
            label += ` (${price}€)`;
        }

        return label;
    };

    const styles = {
        section: {
            background: 'white',
            borderRadius: '12px',
            padding: '12px 24px 24px',
            marginBottom: '12px',
            boxShadow: '0 1px 3px rgba(0,0,0,0.1)'
        },
        sectionTitle: {
            fontSize: '20px',
            fontWeight: '600',
            color: '#1f2937',
            margin: 0,
            display: 'flex',
            alignItems: 'center',
            gap: '12px',
            marginBottom: '20px'
        },
        grid: {
            marginTop: '32px',
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))',
            gap: '16px'
        },
        infoCard: {
            background: '#f8fafc',
            padding: '20px',
            borderRadius: '8px',
            border: '1px solid #e5e7eb'
        },
        infoItem: {
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            padding: '12px 0',
            borderBottom: '1px solid #f3f4f6'
        },
        infoLabel: {
            fontWeight: '600',
            color: '#374151',
            fontSize: '14px'
        },
        infoValue: {
            color: '#6b7280',
            fontSize: '14px',
            textAlign: 'right'
        },
        iconText: {
            display: 'flex',
            alignItems: 'center',
            gap: '8px',
            marginBottom: '8px'
        },
        statsGrid: {
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))',
            gap: '16px',
            marginBottom: '20px'
        },
        statCard: {
            background: '#f8fafc',
            padding: '16px',
            borderRadius: '8px',
            border: '1px solid #e5e7eb',
            textAlign: 'center'
        },
        statValue: {
            fontSize: '24px',
            fontWeight: 'bold',
            color: '#7c3aed',
            marginBottom: '4px'
        },
        statLabel: {
            fontSize: '12px',
            color: '#6b7280',
            textTransform: 'uppercase'
        },
        serviceCategory: {
            marginTop: '0px',
            padding: '16px',
            background: '#f0f9ff',
            borderRadius: '8px',
            border: '1px solid #bae6fd'
        },
        categoryTitle: {
            fontSize: '16px',
            fontWeight: '600',
            color: '#1e40af',
            marginBottom: '12px',
            display: 'flex',
            alignItems: 'center',
            gap: '8px'
        },
        serviceItems: {
            display: 'flex',
            flexWrap: 'wrap',
            gap: '8px'
        },
        serviceItem: {
            padding: '6px 12px',
            background: '#dbeafe',
            color: '#1e40af',
            borderRadius: '16px',
            fontSize: '12px',
            fontWeight: '500'
        },
        noData: {
            textAlign: 'center',
            padding: '40px',
            color: '#9ca3af',
            fontStyle: 'italic'
        },
        booleanItem: {
            display: 'flex',
            alignItems: 'center',
            gap: '8px',
            marginBottom: '8px'
        },
        loadingContainer: {
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            padding: '40px'
        },
        serviceWithEquipment: {
            display: 'flex',
            flexDirection: 'column',
            gap: '4px',
            marginBottom: '8px'
        },
        equipmentList: {
            display: 'flex',
            flexWrap: 'wrap',
            gap: '4px',
            marginLeft: '24px'
        },
        equipmentItem: {
            padding: '4px 8px',
            background: '#f3f4f6',
            borderRadius: '12px',
            fontSize: '11px',
            color: '#6b7280'
        }
    };

    const hotel = product.hotel;

    if (!hotel) {
        return (
            <div style={{ background: 'white', borderRadius: '12px', padding: '24px', marginBottom: '12px', boxShadow: '0 1px 3px rgba(0,0,0,0.1)' }}>
                <h2 style={{ fontSize: '20px', fontWeight: '600', color: '#1f2937', marginBottom: '20px', display: 'flex', alignItems: 'center', gap: '12px' }}>
                    <Building size={20} />
                    {t('hotelDetails')}
                </h2>
                <p style={{ textAlign: 'center', padding: '40px', color: '#9ca3af', fontStyle: 'italic' }}>
                    {t('noHotelDetails')}
                </p>
            </div>
        );
    }

    // 1. SECTION SPORT
    const SportSection = () => {
        const [sportNames, setSportNames] = useState({});

        useEffect(() => {
            const fetchSportNames = async () => {
                if (!hotel.product_hotel_sport_attraction_service) return;

                const token = localStorage.getItem('authToken') || sessionStorage.getItem('authToken');
                const services = hotel.product_hotel_sport_attraction_service;

                const promises = services.map(async (service) => {
                    const equipmentId = service.equipment_sport_attraction_service_id;
                    if (!equipmentId) return null;

                    try {
                        const response = await fetch(`${API_BASE_URL}/reference-data/equipement-sport-attraction-services/${equipmentId}`, {
                            headers: {
                                'Authorization': `Bearer ${token}`,
                                'Accept': 'application/json'
                            }
                        });

                        if (!response.ok) return `Équipement ${equipmentId}`;

                        const data = await response.json();
                        return {
                            id: equipmentId,
                            name: data[lang] || data.en || data.fr || data.es || `Équipement ${equipmentId}`
                        };
                    } catch (error) {
                        return { id: equipmentId, name: `Équipement ${equipmentId}` };
                    }
                });

                const results = await Promise.all(promises);
                const namesMap = {};
                results.forEach(result => {
                    if (result) namesMap[result.id] = result.name;
                });

                setSportNames(namesMap);
            };

            fetchSportNames();
        }, []);

        if (!hotel.product_hotel_sport_attraction_service || hotel.product_hotel_sport_attraction_service.length === 0) {
            return null;
        }

        const groups = {};
        hotel.product_hotel_sport_attraction_service.forEach(service => {
            const serviceId = service.sport_attraction_service_id;
            const equipmentId = service.equipment_sport_attraction_service_id;

            if (!groups[serviceId]) groups[serviceId] = [];
            if (equipmentId) groups[serviceId].push(equipmentId);
        });

        return (
            <div style={{ padding: '16px', background: '#f0f9ff', borderRadius: '8px', border: '1px solid #bae6fd' }}>
                <div style={{ fontSize: '16px', fontWeight: '600', color: '#1e40af', marginBottom: '12px', display: 'flex', alignItems: 'center', gap: '8px' }}>
                    <Dumbbell size={16} />
                    {t('sportsActivities')}
                </div>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                    {Object.entries(groups).map(([serviceId, equipmentIds]) => (
                        <div key={serviceId}>
                            <div style={{ fontWeight: '500', marginBottom: '4px' }}>
                                {t('sportService')} {serviceId}
                            </div>
                            {equipmentIds.length > 0 && (
                                <div style={{ display: 'flex', flexWrap: 'wrap', gap: '4px', marginLeft: '12px' }}>
                                    {equipmentIds.map((equipmentId, idx) => (
                                        <span key={idx} style={{ padding: '4px 8px', background: '#dbeafe', color: '#1e40af', borderRadius: '12px', fontSize: '11px' }}>
                                            {sportNames[equipmentId] || `${t('equipment')} ${equipmentId}`}
                                        </span>
                                    ))}
                                </div>
                            )}
                        </div>
                    ))}
                </div>
            </div>
        );
    };

    // 2. SECTION WELLNESS
    const WellnessSection = () => {
        const [wellnessNames, setWellnessNames] = useState({});

        useEffect(() => {
            const fetchWellnessNames = async () => {
                if (!hotel.product_hotel_wellness_service) return;

                const token = localStorage.getItem('authToken') || sessionStorage.getItem('authToken');
                const services = hotel.product_hotel_wellness_service;

                const promises = services.map(async (service) => {
                    const equipmentId = service.equipement_wellness_service_id;
                    if (!equipmentId) return null;

                    try {
                        const response = await fetch(`${API_BASE_URL}/reference-data/equipement-wellness-services/${equipmentId}`, {
                            headers: {
                                'Authorization': `Bearer ${token}`,
                                'Accept': 'application/json'
                            }
                        });

                        if (!response.ok) return `Équipement ${equipmentId}`;

                        const data = await response.json();
                        return {
                            id: equipmentId,
                            name: data[lang] || data.en || data.fr || data.es || `Équipement ${equipmentId}`
                        };
                    } catch (error) {
                        return { id: equipmentId, name: `Équipement ${equipmentId}` };
                    }
                });

                const results = await Promise.all(promises);
                const namesMap = {};
                results.forEach(result => {
                    if (result) namesMap[result.id] = result.name;
                });

                setWellnessNames(namesMap);
            };

            fetchWellnessNames();
        }, []);

        if (!hotel.product_hotel_wellness_service || hotel.product_hotel_wellness_service.length === 0) {
            return null;
        }

        const groups = {};
        hotel.product_hotel_wellness_service.forEach(service => {
            const serviceId = service.wellness_sewstrvice_id;
            const equipmentId = service.equipement_wellness_service_id;

            if (!groups[serviceId]) groups[serviceId] = [];
            if (equipmentId) groups[serviceId].push(equipmentId);
        });

        return (
            <div style={{ padding: '16px', background: '#f0f9ff', borderRadius: '8px', border: '1px solid #bae6fd' }}>
                <div style={{ fontSize: '16px', fontWeight: '600', color: '#1e40af', marginBottom: '12px', display: 'flex', alignItems: 'center', gap: '8px' }}>
                    <Heart size={16} />
                    {t('wellnessServices')}
                </div>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                    {Object.entries(groups).map(([serviceId, equipmentIds]) => (
                        <div key={serviceId}>
                            <div style={{ fontWeight: '500', marginBottom: '4px' }}>
                                {t('wellnessService')} {serviceId}
                            </div>
                            {equipmentIds.length > 0 && (
                                <div style={{ display: 'flex', flexWrap: 'wrap', gap: '4px', marginLeft: '12px' }}>
                                    {equipmentIds.map((equipmentId, idx) => (
                                        <span key={idx} style={{ padding: '4px 8px', background: '#dbeafe', color: '#1e40af', borderRadius: '12px', fontSize: '11px' }}>
                                            {wellnessNames[equipmentId] || `${t('equipment')} ${equipmentId}`}
                                        </span>
                                    ))}
                                </div>
                            )}
                        </div>
                    ))}
                </div>
            </div>
        );
    };

    // 3. SECTION BUSINESS
    const BusinessSection = () => {
        const [businessNames, setBusinessNames] = useState({});

        useEffect(() => {
            const fetchBusinessNames = async () => {
                if (!hotel.product_hotel_business_service) return;

                const token = localStorage.getItem('authToken') || sessionStorage.getItem('authToken');
                const services = hotel.product_hotel_business_service;

                const promises = services.map(async (service) => {
                    const equipmentId = service.equipement_business_service_id;
                    if (!equipmentId) return null;

                    try {
                        const response = await fetch(`${API_BASE_URL}/reference-data/equipement-business-service/${equipmentId}`, {
                            headers: {
                                'Authorization': `Bearer ${token}`,
                                'Accept': 'application/json'
                            }
                        });

                        if (!response.ok) return `Équipement ${equipmentId}`;

                        const data = await response.json();
                        return {
                            id: equipmentId,
                            name: data[lang] || data.en || data.fr || data.es || `Équipement ${equipmentId}`
                        };
                    } catch (error) {
                        return { id: equipmentId, name: `Équipement ${equipmentId}` };
                    }
                });

                const results = await Promise.all(promises);
                const namesMap = {};
                results.forEach(result => {
                    if (result) namesMap[result.id] = result.name;
                });

                setBusinessNames(namesMap);
            };

            fetchBusinessNames();
        }, []);

        if (!hotel.product_hotel_business_service || hotel.product_hotel_business_service.length === 0) {
            return null;
        }

        const groups = {};
        hotel.product_hotel_business_service.forEach(service => {
            const serviceId = service.business_service_id;
            const equipmentId = service.equipement_business_service_id;

            if (!groups[serviceId]) groups[serviceId] = [];
            if (equipmentId) groups[serviceId].push(equipmentId);
        });

        return (
            <div style={{ padding: '16px', background: '#f0f9ff', borderRadius: '8px', border: '1px solid #bae6fd' }}>
                <div style={{ fontSize: '16px', fontWeight: '600', color: '#1e40af', marginBottom: '12px', display: 'flex', alignItems: 'center', gap: '8px' }}>
                    <Briefcase size={16} />
                    {t('businessServices')}
                </div>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                    {Object.entries(groups).map(([serviceId, equipmentIds]) => (
                        <div key={serviceId}>
                            <div style={{ fontWeight: '500', marginBottom: '4px' }}>
                                {t('businessService')} {serviceId}
                            </div>
                            {equipmentIds.length > 0 && (
                                <div style={{ display: 'flex', flexWrap: 'wrap', gap: '4px', marginLeft: '12px' }}>
                                    {equipmentIds.map((equipmentId, idx) => (
                                        <span key={idx} style={{ padding: '4px 8px', background: '#dbeafe', color: '#1e40af', borderRadius: '12px', fontSize: '11px' }}>
                                            {businessNames[equipmentId] || `${t('equipment')} ${equipmentId}`}
                                        </span>
                                    ))}
                                </div>
                            )}
                        </div>
                    ))}
                </div>
            </div>
        );
    };

    // 4. SECTION FAMILY
    const FamilySection = () => {
        const [familyNames, setFamilyNames] = useState({});

        useEffect(() => {
            const fetchFamilyNames = async () => {
                if (!hotel.product_hotel_family_romantique_service) return;

                const token = localStorage.getItem('authToken') || sessionStorage.getItem('authToken');
                const services = hotel.product_hotel_family_romantique_service;

                const promises = services.map(async (service) => {
                    const equipmentId = service.equipement_family_romantique_service_id;
                    if (!equipmentId) return null;

                    try {
                        const response = await fetch(`${API_BASE_URL}/reference-data/equipement-family-romantique-service/${equipmentId}`, {
                            headers: {
                                'Authorization': `Bearer ${token}`,
                                'Accept': 'application/json'
                            }
                        });

                        if (!response.ok) return `Équipement ${equipmentId}`;

                        const data = await response.json();
                        return {
                            id: equipmentId,
                            name: data[lang] || data.en || data.fr || data.es || `Équipement ${equipmentId}`
                        };
                    } catch (error) {
                        return { id: equipmentId, name: `Équipement ${equipmentId}` };
                    }
                });

                const results = await Promise.all(promises);
                const namesMap = {};
                results.forEach(result => {
                    if (result) namesMap[result.id] = result.name;
                });

                setFamilyNames(namesMap);
            };

            fetchFamilyNames();
        }, []);

        if (!hotel.product_hotel_family_romantique_service || hotel.product_hotel_family_romantique_service.length === 0) {
            return null;
        }

        const groups = {};
        hotel.product_hotel_family_romantique_service.forEach(service => {
            const serviceId = service.family_romantique_service_id;
            const equipmentId = service.equipement_family_romantique_service_id;

            if (!groups[serviceId]) groups[serviceId] = [];
            if (equipmentId) groups[serviceId].push(equipmentId);
        });

        return (
            <div style={{ padding: '16px', background: '#f0f9ff', borderRadius: '8px', border: '1px solid #bae6fd' }}>
                <div style={{ fontSize: '16px', fontWeight: '600', color: '#1e40af', marginBottom: '12px', display: 'flex', alignItems: 'center', gap: '8px' }}>
                    <UsersIcon size={16} />
                    {t('familyRomanticServices')}
                </div>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                    {Object.entries(groups).map(([serviceId, equipmentIds]) => (
                        <div key={serviceId}>
                            <div style={{ fontWeight: '500', marginBottom: '4px' }}>
                                {t('familyService')} {serviceId}
                            </div>
                            {equipmentIds.length > 0 && (
                                <div style={{ display: 'flex', flexWrap: 'wrap', gap: '4px', marginLeft: '12px' }}>
                                    {equipmentIds.map((equipmentId, idx) => (
                                        <span key={idx} style={{ padding: '4px 8px', background: '#dbeafe', color: '#1e40af', borderRadius: '12px', fontSize: '11px' }}>
                                            {familyNames[equipmentId] || `${t('equipment')} ${equipmentId}`}
                                        </span>
                                    ))}
                                </div>
                            )}
                        </div>
                    ))}
                </div>
            </div>
        );
    };

    // 5. SECTION NATURE
    const NatureSection = () => {
        const [natureNames, setNatureNames] = useState({});

        useEffect(() => {
            const fetchNatureNames = async () => {
                if (!hotel.product_hotel_nature_environment_service) return;

                const token = localStorage.getItem('authToken') || sessionStorage.getItem('authToken');
                const services = hotel.product_hotel_nature_environment_service;

                const promises = services.map(async (service) => {
                    const equipmentId = service.equipment_nature_environment_id;
                    if (!equipmentId) return null;

                    try {
                        const response = await fetch(`${API_BASE_URL}/reference-data/equipment-nature-environments/${equipmentId}`, {
                            headers: {
                                'Authorization': `Bearer ${token}`,
                                'Accept': 'application/json'
                            }
                        });

                        if (!response.ok) return `Équipement ${equipmentId}`;

                        const data = await response.json();
                        return {
                            id: equipmentId,
                            name: data[lang] || data.en || data.fr || data.es || `Équipement ${equipmentId}`
                        };
                    } catch (error) {
                        return { id: equipmentId, name: `Équipement ${equipmentId}` };
                    }
                });

                const results = await Promise.all(promises);
                const namesMap = {};
                results.forEach(result => {
                    if (result) namesMap[result.id] = result.name;
                });

                setNatureNames(namesMap);
            };

            fetchNatureNames();
        }, []);

        if (!hotel.product_hotel_nature_environment_service || hotel.product_hotel_nature_environment_service.length === 0) {
            return null;
        }

        const groups = {};
        hotel.product_hotel_nature_environment_service.forEach(service => {
            const serviceId = service.nature_environment_id;
            const equipmentId = service.equipment_nature_environment_id;

            if (!groups[serviceId]) groups[serviceId] = [];
            if (equipmentId) groups[serviceId].push(equipmentId);
        });

        return (
            <div style={{ padding: '16px', background: '#f0f9ff', borderRadius: '8px', border: '1px solid #bae6fd' }}>
                <div style={{ fontSize: '16px', fontWeight: '600', color: '#1e40af', marginBottom: '12px', display: 'flex', alignItems: 'center', gap: '8px' }}>
                    <Trees size={16} />
                    {t('natureEnvironment')}
                </div>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                    {Object.entries(groups).map(([serviceId, equipmentIds]) => (
                        <div key={serviceId}>
                            <div style={{ fontWeight: '500', marginBottom: '4px' }}>
                                {t('natureService')} {serviceId}
                            </div>
                            {equipmentIds.length > 0 && (
                                <div style={{ display: 'flex', flexWrap: 'wrap', gap: '4px', marginLeft: '12px' }}>
                                    {equipmentIds.map((equipmentId, idx) => (
                                        <span key={idx} style={{ padding: '4px 8px', background: '#dbeafe', color: '#1e40af', borderRadius: '12px', fontSize: '11px' }}>
                                            {natureNames[equipmentId] || `${t('equipment')} ${equipmentId}`}
                                        </span>
                                    ))}
                                </div>
                            )}
                        </div>
                    ))}
                </div>
            </div>
        );
    };

    return (
        <>
            {/* Section Détails de l'hôtel */}
            <div style={styles.section}>
                <h2 style={styles.sectionTitle}>
                    <Building size={20} />
                    {t('hotelDetails')}
                </h2>

                {/* Statistiques rapides */}
                <div style={styles.statsGrid}>
                    <div style={styles.statCard}>
                        <div style={styles.statValue}>{hotel.number_of_rooms || 0}</div>
                        <div style={styles.statLabel}>{t('rooms')}</div>
                    </div>
                    <div style={styles.statCard}>
                        <div style={styles.statValue}>{hotel.number_of_restaurant || 0}</div>
                        <div style={styles.statLabel}>{t('restaurants')}</div>
                    </div>
                    <div style={styles.statCard}>
                        <div style={styles.statValue}>{hotel.number_of_swimmingpool || 0}</div>
                        <div style={styles.statLabel}>{t('swimmingPools')}</div>
                    </div>
                    <div style={styles.statCard}>
                        <div style={styles.statValue}>{hotel.construction_year || 'N/A'}</div>
                        <div style={styles.statLabel}>{t('constructionYear')}</div>
                    </div>
                </div>

                <div style={styles.grid}>
                    {/* Informations de base */}
                    <div style={styles.infoCard}>
                        <h3 style={{ marginBottom: '16px', color: '#1f2937' }}>{t('basicInfo')}</h3>
                        {hotel.construction_year && (
                            <div style={styles.infoItem}>
                                <span style={styles.infoLabel}>{t('constructionYear')}</span>
                                <span style={styles.infoValue}>{hotel.construction_year}</span>
                            </div>
                        )}
                        {hotel.last_renovation_year && (
                            <div style={styles.infoItem}>
                                <span style={styles.infoLabel}>{t('lastRenovation')}</span>
                                <span style={styles.infoValue}>{hotel.last_renovation_year}</span>
                            </div>
                        )}
                        {hotel.number_of_floor > 0 && (
                            <div style={styles.infoItem}>
                                <span style={styles.infoLabel}>{t('floors')}</span>
                                <span style={styles.infoValue}>{hotel.number_of_floor}</span>
                            </div>
                        )}
                        {hotel.pricing_type && (
                            <div style={styles.infoItem}>
                                <span style={styles.infoLabel}>{t('pricingType')}</span>
                                <span style={styles.infoValue}>{t(hotel.pricing_type)}</span>
                            </div>
                        )}
                        {hotel.meal_age_autorizate > 0 && (
                            <div style={styles.infoItem}>
                                <span style={styles.infoLabel}>{t('mealAgeRequirement')}</span>
                                <span style={styles.infoValue}>{hotel.meal_age_autorizate} {t('years')}</span>
                            </div>
                        )}
                    </div>

                    {/* Horaires */}
                    <div style={styles.infoCard}>
                        <h3 style={{ marginBottom: '16px', color: '#1f2937' }}>{t('schedules')}</h3>
                        {hotel.checkin_time && (
                            <div style={styles.infoItem}>
                                <span style={styles.infoLabel}>{t('checkinTime')}</span>
                                <span style={styles.infoValue}>
                                    <Clock size={14} style={{ marginRight: '4px' }} />
                                    {formatTime(hotel.checkin_time)}
                                </span>
                            </div>
                        )}
                        {hotel.checkout_time && (
                            <div style={styles.infoItem}>
                                <span style={styles.infoLabel}>{t('checkoutTime')}</span>
                                <span style={styles.infoValue}>
                                    <Clock size={14} style={{ marginRight: '4px' }} />
                                    {formatTime(hotel.checkout_time)}
                                </span>
                            </div>
                        )}
                        {hotel.min_check_in_age > 0 && (
                            <div style={styles.infoItem}>
                                <span style={styles.infoLabel}>{t('minCheckInAge')}</span>
                                <span style={styles.infoValue}>{hotel.min_check_in_age} {t('years')}</span>
                            </div>
                        )}
                    </div>


                </div>

                {/* Services Booléens - Première ligne */}
                <div style={{ marginTop: '24px' }}>
                    <h3 style={{ marginBottom: '16px', color: '#1f2937' }}>{t('basicServices')}</h3>
                    <div style={styles.grid}>
                        <div style={styles.infoCard}>
                            <div style={styles.booleanItem}>
                                {hotel.is_has_reception ? <Check color="#10b981" size={16} /> : <X color="#ef4444" size={16} />}
                                {t('reception24h')}
                            </div>
                            <div style={styles.booleanItem}>
                                {hotel.is_has_reception_virtual ? <Check color="#10b981" size={16} /> : <X color="#ef4444" size={16} />}
                                {t('virtualReception')}
                            </div>
                            <div style={styles.booleanItem}>
                                {hotel.is_will_welcome_travelers ? <Check color="#10b981" size={16} /> : <X color="#ef4444" size={16} />}
                                {t('welcomeService')}
                            </div>
                        </div>

                        <div style={styles.infoCard}>
                            <div style={styles.booleanItem}>
                                {hotel.is_early_arrival_available ? <Check color="#10b981" size={16} /> : <X color="#ef4444" size={16} />}
                                {t('earlyArrival')}
                            </div>
                            <div style={styles.booleanItem}>
                                {hotel.is_late_arrival_available ? <Check color="#10b981" size={16} /> : <X color="#ef4444" size={16} />}
                                {t('lateArrival')}
                            </div>
                            <div style={styles.booleanItem}>
                                {hotel.is_has_internet_connection ? <Check color="#10b981" size={16} /> : <X color="#ef4444" size={16} />}
                                {t('internetConnection')}
                            </div>
                        </div>
                    </div>
                </div>

                {/* Services détaillés */}
                <div style={{ marginTop: '24px' }}>
                                        <h3 style={{ marginBottom: '16px', color: '#1f2937' }}>{t('detailedServices')}</h3>
                    <div style={styles.grid}>
                        {/* Procédures d'accueil */}
                        {hotel.product_hotel_welcom_procedure && hotel.product_hotel_welcom_procedure.length > 0 && (
                            <div style={styles.serviceCategory}>
                                <div style={styles.categoryTitle}>
                                    <UsersIcon size={16} />
                                    {t('welcomeProcedures')}
                                </div>
                                <div style={styles.serviceItems}>
                                    {hotel.product_hotel_welcom_procedure.map((procedure, index) => (
                                        <span key={index} style={styles.serviceItem}>
                                            {getServiceLabel('welcome_procedure', procedure.product_welcom_procedure_id)}
                                        </span>
                                    ))}
                                </div>
                            </div>
                        )}

                        {/* Services Internet */}
                        {hotel.product_hotel_internet_service && hotel.product_hotel_internet_service.length > 0 && (
                            <div style={styles.serviceCategory}>
                                <div style={styles.categoryTitle}>
                                    <Wifi size={16} />
                                    {t('internetServices')}
                                </div>
                                <div style={styles.serviceItems}>
                                    {hotel.product_hotel_internet_service.map((service, index) => (
                                        <span key={index} style={styles.serviceItem}>
                                            {getServiceLabel('internet_service', service.product_internet_service_id, service.price)}
                                        </span>
                                    ))}
                                </div>
                            </div>
                        )}

                        {/* Parking */}
                        {hotel.product_hotel_parking_service && hotel.product_hotel_parking_service.length > 0 && (
                            <div style={styles.serviceCategory}>
                                <div style={styles.categoryTitle}>
                                    <Car size={16} />
                                    {t('parkingServices')}
                                </div>
                                <div style={styles.serviceItems}>
                                    {hotel.product_hotel_parking_service.map((service, index) => (
                                        <span key={index} style={styles.serviceItem}>
                                            {getServiceLabel('parking_service', service.product_parking_service_id, service.price)}
                                        </span>
                                    ))}
                                </div>
                            </div>
                        )}

                        {/* Piscines */}
                        {hotel.product_hotel_swiming_pool_service && hotel.product_hotel_swiming_pool_service.length > 0 && (
                            <div style={styles.serviceCategory}>
                                <div style={styles.categoryTitle}>
                                    <Wind size={16} />
                                    {t('swimmingPools')}
                                </div>
                                <div style={styles.serviceItems}>
                                    {hotel.product_hotel_swiming_pool_service.map((pool, index) => (
                                        <span key={index} style={styles.serviceItem}>
                                            {getServiceLabel('swimming_pool_service', pool.product_swimming_pool_service_id)}
                                            {pool.start_time && ` (${formatTime(pool.start_time)}-${formatTime(pool.end_time)})`}
                                            {pool.cost > 0 && ` - ${pool.cost}€`}
                                        </span>
                                    ))}
                                </div>
                            </div>
                        )}
                    </div>



                    <div style={{ marginTop: '16px' }}>
                        <div style={{ marginTop: '32px', display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))', gap: '16px' }}>
                            <SportSection />
                            <WellnessSection />
                            <BusinessSection />
                            <FamilySection />
                            <NatureSection />
                        </div>
                    </div>
                </div>
            </div>

        </>

    );
};

export default HotelView;